/**
 * RCC Surface #2 — SAR Drafting Workspace.
 *
 * FSR drafts narrative + attaches evidence. Writes draft to Dataverse via the
 * /v3/qc/* routes (SAR drafts are modeled as QC exception tasks with a SAR
 * subcategory). External filing via FinCEN BSA E-Filing is manual in v3.0.
 */

import { useCallback, useState } from 'react';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button, Pill } from '@/components/primitives';
import { SurfaceHeader } from '../components/SurfaceHeader';

type DraftStatus = 'idle' | 'saving' | 'saved' | 'error';

export function SarDraft(): JSX.Element {
  const apiClient = useApiClient();
  const { user } = useAuth();
  const [accountNumber, setAccountNumber] = useState<string>('');
  const [narrative, setNarrative] = useState<string>('');
  const [status, setStatus] = useState<DraftStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const saveDraft = useCallback(async (): Promise<void> => {
    const trimmed = narrative.trim();
    if (!trimmed) {
      setError('Narrative is required.');
      setStatus('error');
      return;
    }
    if (!accountNumber.trim()) {
      setError('Account number is required.');
      setStatus('error');
      return;
    }

    setStatus('saving');
    setError(null);
    try {
      await apiClient.retail.qc.addNote({
        OfficerEmail: user?.upn ?? 'unknown@mypcb.com',
        AccountId: accountNumber.trim(),
        NoteText: `SAR-DRAFT\n\n${trimmed}`,
      });
      void apiClient.retail.events
        .write({
          eventType: 'sar-drafted',
          eventCategory: 'compliance',
          severity: 'warn',
          actor: user?.upn ?? 'unknown',
          payloadJson: JSON.stringify({ accountNumber: accountNumber.trim(), narrativeLength: trimmed.length }),
        })
        .catch(() => undefined);
      setStatus('saved');
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Failed to save SAR draft');
    }
  }, [accountNumber, apiClient, narrative, user]);

  return (
    <div>
      <SurfaceHeader
        title="SAR Drafting Workspace"
        scope="Draft Suspicious Activity Report narrative. Saves to Dataverse QC exception as a SAR note. External filing via FinCEN BSA E-Filing is manual."
        surfaceNumber={2}
        role="FSR"
        actions={
          status === 'saved' ? (
            <Pill variant="ok">Draft saved</Pill>
          ) : status === 'saving' ? (
            <Pill variant="info">Saving…</Pill>
          ) : undefined
        }
      />

      <div style={{ display: 'grid', gap: 'var(--sp-6)', maxWidth: 720 }}>
        <div>
          <label
            htmlFor="sar-account"
            style={{
              display: 'block',
              fontSize: 11,
              textTransform: 'uppercase',
              color: 'var(--ink-500)',
              marginBottom: 'var(--sp-3)',
            }}
          >
            Account number
          </label>
          <input
            id="sar-account"
            type="text"
            value={accountNumber}
            onChange={(e) => setAccountNumber(e.target.value)}
            placeholder="Core Director account number"
            style={{
              width: '100%',
              padding: 'var(--sp-4)',
              border: '1px solid var(--ink-300)',
              borderRadius: 'var(--r-md)',
              fontSize: 13,
            }}
          />
        </div>
        <div>
          <label
            htmlFor="sar-narrative"
            style={{
              display: 'block',
              fontSize: 11,
              textTransform: 'uppercase',
              color: 'var(--ink-500)',
              marginBottom: 'var(--sp-3)',
            }}
          >
            Narrative
          </label>
          <textarea
            id="sar-narrative"
            rows={12}
            value={narrative}
            onChange={(e) => setNarrative(e.target.value)}
            placeholder="Who, what, when, where, why, how. Describe the suspicious activity in detail."
            style={{
              width: '100%',
              padding: 'var(--sp-4)',
              border: '1px solid var(--ink-300)',
              borderRadius: 'var(--r-md)',
              fontFamily: 'var(--font-sans)',
              fontSize: 13,
              lineHeight: 1.5,
              resize: 'vertical',
            }}
          />
          <div style={{ marginTop: 'var(--sp-2)', fontSize: 11, color: 'var(--ink-500)' }}>
            {narrative.length} characters · min 5 yr retention per BSA.
          </div>
        </div>

        {error && (
          <div
            role="alert"
            style={{
              padding: 'var(--sp-5)',
              background: 'var(--err-bg)',
              color: 'var(--err-text)',
              borderRadius: 'var(--r-md)',
              fontSize: 12,
            }}
          >
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 'var(--sp-4)' }}>
          <Button variant="primary" onClick={() => void saveDraft()} disabled={status === 'saving'}>
            Save draft
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              setNarrative('');
              setAccountNumber('');
              setStatus('idle');
              setError(null);
            }}
          >
            Clear
          </Button>
        </div>
      </div>
    </div>
  );
}
